import { canonical } from "./seo";
import { serviceSchema, faqSchema, type Faq } from "./service-schema";
import { ogImageMeta } from "./og-image";
import { applySeoOverrides, type PageSeoOverride } from "./seo-overrides";

/**
 * Build the `head()` return value for a service page.
 * Includes per-page title/desc, canonical, og:* (incl. branded share card),
 * Service JSON-LD with AggregateRating, and FAQPage JSON-LD when faqs are provided.
 */
export function serviceHead(opts: {
  slug: string;
  title: string;
  description: string;
  serviceName: string;
  faqs?: Faq[];
  rating?: number;
  reviewCount?: number;
  /** Optional override for the OG share card. Defaults to /og/og-<slug>.png. */
  ogImage?: string;
  /** Admin (Pages CMS) SEO overrides; non-empty values win over the args above. */
  override?: PageSeoOverride | null;
}) {
  const url = canonical(opts.slug);
  const scripts: Array<{ type: string; children: string }> = [
    {
      type: "application/ld+json",
      children: JSON.stringify(
        serviceSchema({
          name: opts.serviceName,
          slug: opts.slug,
          description: opts.description,
          rating: opts.rating,
          reviewCount: opts.reviewCount,
        }),
      ),
    },
  ];
  if (opts.faqs && opts.faqs.length > 0) {
    scripts.push({
      type: "application/ld+json",
      children: JSON.stringify(faqSchema(opts.faqs)),
    });
  }
  return applySeoOverrides({
    meta: [
      { title: opts.title },
      { name: "description", content: opts.description },
      { property: "og:title", content: opts.title },
      { property: "og:description", content: opts.description },
      { property: "og:url", content: url },
      { property: "og:type", content: "website" },
      { name: "twitter:title", content: opts.title },
      { name: "twitter:description", content: opts.description },
      ...ogImageMeta(opts.ogImage ?? opts.slug),
    ],
    links: [{ rel: "canonical", href: url }],
    scripts,
  }, opts.override);
}
